import { FieldError, FieldErrors } from 'react-hook-form';
import { validationError } from '../validation';
import { get, isInteger, set, toNumber } from 'lodash';
import { EntityPanelFormVehicleBaseKey, EntityPanelVehiclesForm } from '../types';
import { CadFeatureMap, DexRegionEnumType, EventPanelOptions } from '@mark43/cad-api';

export interface ValidateVehiclesProps {
    form: EntityPanelVehiclesForm;
    baseKey: EntityPanelFormVehicleBaseKey;
    enabledFeatures: CadFeatureMap;
    eventPanelOptions?: EventPanelOptions;
    existingErrors: FieldErrors<EntityPanelVehiclesForm>;
    departmentDexRegion?: DexRegionEnumType;
}

const INVALID_VIN_CHARACTERS = /[qoi]/i;

export function isValidVin(vinNumber: null | undefined | string): boolean {
    if (vinNumber && vinNumber.length < 5) return false;
    if (vinNumber && vinNumber.length > 32) return false;
    if (vinNumber && vinNumber.length === 17 && INVALID_VIN_CHARACTERS.test(vinNumber)) {
        return false;
    }
    return true;
}

export const validateEntityPanelVehicles = ({
    form,
    baseKey,
    enabledFeatures,
    eventPanelOptions,
    existingErrors,
    departmentDexRegion,
}: ValidateVehiclesProps) => {
    form[baseKey].forEach((vehicle, index) => {
        const localErrors: Partial<
            Record<keyof EntityPanelVehiclesForm[typeof baseKey][number], FieldError>
        > = {};
        if (vehicle.queryInDex) {
            if (departmentDexRegion === 'NM_NMLETS') {
                if (
                    !(vehicle.plateNumber && vehicle.plateType && vehicle.plateYear) &&
                    !vehicle.vinNumber
                ) {
                    localErrors.queryInDex = validationError(
                        'Plate #, Plate Type, and Plate Year or VIN required'
                    );
                }
            } else if (departmentDexRegion === 'NJ_CJIS') {
                if (
                    !(vehicle.plateNumber && vehicle.stateOption) &&
                    !(vehicle.vinNumber && vehicle.stateOption)
                ) {
                    localErrors.queryInDex = validationError(
                        'Plate # and State or VIN and State required'
                    );
                }
            } else if (departmentDexRegion === 'TN_TIES') {
                if (!(vehicle.plateNumber && vehicle.stateOption) && !vehicle.vinNumber) {
                    localErrors.queryInDex = validationError('Plate # and State or VIN required');
                }
            } else {
                if (!vehicle.plateNumber && !vehicle.vinNumber) {
                    localErrors.queryInDex = validationError('Plate # or VIN required');
                }
            }
            if (
                enabledFeatures.dexPurposeCode &&
                eventPanelOptions?.purposeCodeOptions.length &&
                !vehicle.isPersisted &&
                !vehicle.purposeCode
            ) {
                localErrors.purposeCode = validationError('Required');
            }
            if (vehicle.plateYear && vehicle.plateYear.length !== 4) {
                localErrors.plateYear = validationError('Must be 4 digits');
            }
        }
        if (!isValidVin(vehicle.vinNumber)) {
            localErrors.vinNumber = validationError('Please enter a valid VIN');
        }
        if (
            vehicle.year !== null &&
            vehicle.year !== undefined &&
            !isInteger(toNumber(vehicle.year))
        ) {
            localErrors.year = validationError('Must be a whole number');
        }
        if (Object.keys(localErrors).length > 0) {
            if (!existingErrors[baseKey]) {
                existingErrors[baseKey] = [];
            }
            if (!get(existingErrors, `${baseKey}[${index}]`)) {
                set(existingErrors, `${baseKey}[${index}]`, localErrors);
            }
        }
    });
};
